// Authentication layer — Cognito sign-in for the single electrician user (Req 1).
//
// The Cognito app client is a PUBLIC SPA client with no secret and only SRP +
// refresh-token flows enabled (see infra/auth-stack.yaml), so the browser signs
// in with SRP via amazon-cognito-identity-js. The SDK persists the ID, access and
// refresh tokens in localStorage under CognitoIdentityServiceProvider.* keys and
// transparently refreshes them from the refresh token.
//
// Token sent to the API: the ID TOKEN. The API Gateway COGNITO_USER_POOLS
// authorizer validates a raw pool JWT from the `Authorization` header; we send the
// ID token (the conventional choice — it carries the user's identity claims) with
// NO "Bearer " prefix, because the Cognito authorizer expects the bare token.
// (Attaching the header lives in api.js.)

import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
} from 'amazon-cognito-identity-js';

import { getConfig } from './config.js';
import { needsRefresh, isTokenExpired, DEFAULT_REFRESH_THRESHOLD_SECONDS } from './lib/token.js';

export class NotAuthenticatedError extends Error {
  constructor(message = 'Not authenticated') {
    super(message);
    this.name = 'NotAuthenticatedError';
  }
}

// Sign-in challenge statuses surfaced to the UI.
export const AuthStatus = Object.freeze({
  SUCCESS: 'SUCCESS',
  NEW_PASSWORD_REQUIRED: 'NEW_PASSWORD_REQUIRED', // AdminCreateUser temp password
  SOFTWARE_TOKEN_MFA: 'SOFTWARE_TOKEN_MFA', // optional TOTP (Req 1.4)
});

let _pool = null;
function pool() {
  if (!_pool) {
    const { userPoolId, userPoolClientId } = getConfig();
    _pool = new CognitoUserPool({ UserPoolId: userPoolId, ClientId: userPoolClientId });
  }
  return _pool;
}

// The CognitoUser mid-challenge (between authenticateUser and completing a new
// password / TOTP step). Held so the follow-up calls reuse the same object.
let _challengeUser = null;

function buildCallbacks(resolve, reject) {
  return {
    onSuccess: () => {
      _challengeUser = null;
      resolve({ status: AuthStatus.SUCCESS });
    },
    onFailure: (err) => {
      _challengeUser = null;
      reject(err instanceof Error ? err : new Error(err && err.message ? err.message : String(err)));
    },
    // First sign-in for an AdminCreateUser account: user must set a real password.
    newPasswordRequired: (userAttributes) => {
      // These attributes are read-only on completeNewPasswordChallenge; drop them.
      delete userAttributes.email_verified;
      delete userAttributes.email;
      delete userAttributes.phone_number_verified;
      resolve({ status: AuthStatus.NEW_PASSWORD_REQUIRED });
    },
    // TOTP MFA is OPTIONAL on the pool; only fires if the user enabled it.
    totpRequired: () => resolve({ status: AuthStatus.SOFTWARE_TOKEN_MFA }),
  };
}

// Start SRP sign-in. Resolves with an AuthStatus; on SUCCESS the SDK has stored a
// valid session. NEW_PASSWORD_REQUIRED / SOFTWARE_TOKEN_MFA must be completed via
// the calls below. Rejects on bad credentials or other failures.
export function signIn(email, password) {
  const username = String(email || '').trim();
  const user = new CognitoUser({ Username: username, Pool: pool() });
  user.setAuthenticationFlowType('USER_SRP_AUTH');
  _challengeUser = user;
  const authDetails = new AuthenticationDetails({ Username: username, Password: password });
  return new Promise((resolve, reject) => {
    user.authenticateUser(authDetails, buildCallbacks(resolve, reject));
  });
}

// Complete the NEW_PASSWORD_REQUIRED challenge from the first sign-in.
export function completeNewPassword(newPassword) {
  if (!_challengeUser) return Promise.reject(new Error('No sign-in in progress'));
  return new Promise((resolve, reject) => {
    _challengeUser.completeNewPasswordChallenge(newPassword, {}, buildCallbacks(resolve, reject));
  });
}

// Complete the TOTP (software token) MFA challenge.
export function confirmTotp(code) {
  if (!_challengeUser) return Promise.reject(new Error('No sign-in in progress'));
  return new Promise((resolve, reject) => {
    _challengeUser.sendMFACode(String(code || '').trim(), buildCallbacks(resolve, reject), 'SOFTWARE_TOKEN_MFA');
  });
}

// Is there a persisted user session (not necessarily still valid)?
export function hasCurrentUser() {
  return !!pool().getCurrentUser();
}

export function getCurrentUserEmail() {
  const user = pool().getCurrentUser();
  return user ? user.getUsername() : null;
}

// Resolve a valid ID token, refreshing transparently when it's expired or nearing
// expiry (Req 1.3, 1.5). Rejects with NotAuthenticatedError when there is no
// session or the refresh token itself is expired/revoked — the caller then routes
// back to login.
export function getValidIdToken(refreshThresholdSeconds = DEFAULT_REFRESH_THRESHOLD_SECONDS) {
  const user = pool().getCurrentUser();
  if (!user) return Promise.reject(new NotAuthenticatedError('No signed-in user'));

  return new Promise((resolve, reject) => {
    // getSession refreshes automatically if the cached tokens are already expired.
    user.getSession((err, session) => {
      if (err) return reject(new NotAuthenticatedError(err.message || 'Session error'));
      if (!session || !session.isValid()) {
        return reject(new NotAuthenticatedError('Session invalid'));
      }

      const idToken = session.getIdToken().getJwtToken();

      // Proactive refresh: still valid but close to expiry -> refresh ahead of the
      // deadline so an in-flight request never fails on a stale token.
      if (!needsRefresh(idToken, refreshThresholdSeconds)) {
        return resolve(idToken);
      }
      user.refreshSession(session.getRefreshToken(), (refreshErr, newSession) => {
        if (refreshErr || !newSession) {
          // Refresh failed: use the current token if it hasn't actually expired,
          // otherwise treat the session as gone.
          if (!isTokenExpired(idToken)) return resolve(idToken);
          return reject(new NotAuthenticatedError('Token refresh failed'));
        }
        resolve(newSession.getIdToken().getJwtToken());
      });
    });
  });
}

// Explicit sign-out (Req 1.5): clears the stored tokens and revokes the refresh
// token (the pool has token revocation enabled).
export function signOut() {
  const user = pool().getCurrentUser();
  if (user) user.signOut();
  _challengeUser = null;
}
